const INIT_ATTRIBUTE = 'data-toc-initialized';

function getHeaderOffsetPx() {
  try {
    const val = getComputedStyle(document.body).getPropertyValue('--header-height') ||
                getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '';
    const header = parseFloat(val.replace('px', '').trim()) || 80;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    return header + 20 + (isMobile ? 60 : 0);
  } catch {
    return 100;
  }
}

/**
 * @param {HTMLElement} container
 */
function setupBlogPostAnchorsObserver(container) {
  if (!container || container.hasAttribute(INIT_ATTRIBUTE)) return;

  /** @type {HTMLAnchorElement[]} */
  const links = Array.from(container.querySelectorAll('.blog-post-content-anchors__link'));
  if (!links.length) return;

  const headingsMap = new Map();
  for (const link of links) {
    const hashId = link.hash?.slice(1);
    if (!hashId) continue;
    const id = decodeURIComponent(hashId);
    const heading = document.getElementById(id);
    if (heading && !headingsMap.has(id)) headingsMap.set(id, heading);
  }

  /** @type {HTMLElement[]} */
  const headings = Array.from(headingsMap.values());
  if (!headings.length) return;  

  const headerOffset = getHeaderOffsetPx();
  /** @type {HTMLDetailsElement | null} */
  const details = container.querySelector('.blog-post-content-anchors__details');

  /**
   * @param {string | undefined} id
   */
  const setActiveById = (id) => {
    if (!id) return;
    const targetHref = `#${id}`;
    links.forEach(link => link.classList.toggle('active', link.getAttribute('href') === targetHref));

    const activeLink = links.find(link => link.getAttribute('href') === targetHref);
    if (activeLink) {
      scrollActiveLinkIntoView(activeLink, container);
    }
  };

  const scrollActiveLinkIntoView = (activeLink, container) => {
    const contentContainer = activeLink.closest('.blog-post-content-anchors__content') || 
    activeLink.closest('.blog-post-content-anchors__content--accordion');
    if (!contentContainer) return;

    if (contentContainer.classList.contains('blog-post-content-anchors__content--accordion')) {
      const details = container.querySelector('.blog-post-content-anchors__details');
      if (!details || !details.open) return;
    }

    const containerLinks = Array.from(contentContainer.querySelectorAll('.blog-post-content-anchors__link'));
    const isFirstLink = containerLinks.length > 0 && containerLinks[0] === activeLink;

    if (isFirstLink) {
      contentContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const linkRect = activeLink.getBoundingClientRect();
    const containerRect = contentContainer.getBoundingClientRect();
    
    const linkTopRelative = linkRect.top - containerRect.top + contentContainer.scrollTop;
    const linkBottomRelative = linkTopRelative + activeLink.offsetHeight;
    
    const scrollTop = contentContainer.scrollTop;
    const containerHeight = contentContainer.clientHeight;
    const visibleTop = scrollTop;
    const visibleBottom = scrollTop + containerHeight;
    
    const isAboveViewport = linkTopRelative < visibleTop;
    const isBelowViewport = linkBottomRelative > visibleBottom;
    
    if (isAboveViewport) {
      contentContainer.scrollTo({ top: linkTopRelative, behavior: 'smooth' });
    } else if (isBelowViewport) {
      contentContainer.scrollTo({ top: linkBottomRelative - containerHeight, behavior: 'smooth' });
    }
  };

  const computeActiveId = () => {
    let activeId = headings[0]?.id;
    for (const el of headings) {
      if (el.getBoundingClientRect().top - headerOffset <= 1) activeId = el.id;
      else break;
    }
    return activeId;
  };

  let rafId = 0;
  const requestUpdate = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      setActiveById(computeActiveId());
    });
  };

  const observer = new IntersectionObserver(requestUpdate, {
    rootMargin: `${-headerOffset}px 0px 0px 0px`,
    threshold: [0, 0.01, 0.5, 1]
  });
  headings.forEach(el => observer.observe(el));

  ['scroll', 'resize'].forEach(evt => window.addEventListener(evt, requestUpdate, { passive: true }));

  const initActive = () => {
    const hashId = location.hash.slice(1);
    if (!hashId) {
      setActiveById(computeActiveId());
      return;
    }
    const id = decodeURIComponent(hashId);
    setActiveById(document.getElementById(id) ? id : computeActiveId());
  };

  window.addEventListener('hashchange', initActive);
  initActive();

  links.forEach(link => {
    link.addEventListener('click', (/** @type {MouseEvent} */ e) => {
      const hashId = link.hash?.slice(1);
      if (!hashId) return;
      const id = decodeURIComponent(hashId);
      const target = document.getElementById(id);
      if (!target) return;

      if (location.pathname === link.pathname && location.hostname === link.hostname) {
        e.preventDefault();
        window.history.replaceState(null, '', link.hash);
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - headerOffset, behavior: 'smooth' });
        setActiveById(target.id);
      }

      if (link.closest('.blog-post-content-anchors__content--accordion') && details?.open) {
        details.open = false;
      }
    });
  });

  if (details) {
    document.addEventListener('click', (/** @type {MouseEvent} */ e) => {
      const target = /** @type {Node | null} */ (e.target);
      if (details.open && target && !details.contains(target)) details.open = false;
    });
  }

  container.setAttribute(INIT_ATTRIBUTE, 'true');
}

function autoAnchorsDetection() {
  const content = document.querySelector('.blog-post-content');
  if (!content) return;

  const headings = content.querySelectorAll('h2');
  if (headings.length < 2) return;

  headings.forEach((heading, idx) => {
    const text = heading.textContent || 'heading';
    let id = text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    if (heading.id) {
      const originalId = heading.id;
      const escapedId = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        ? CSS.escape(originalId)
        : originalId;
      document.querySelectorAll(`a[href="#${escapedId}"]`).forEach((link) => {
        link.href = `#${id}`;
      });
      heading.id = id;
      return;
    }

    let uniqueId = id;
    let counter = 1;
    while (document.getElementById(uniqueId)) {
      uniqueId = `${id}-${counter++}`;
    }
    heading.id = uniqueId;

    const anchor = document.createElement('a');
    anchor.className = 'blog-post-content-anchors__link';
    anchor.href = `#${uniqueId}`;
    anchor.textContent = heading.textContent || '';

    const anchorWrapper = heading
      .closest('.blog-post-content-wrapper');

    if (!anchorWrapper) return;
    const accordion = anchorWrapper.querySelector('.blog-post-content-anchors__content--accordion');

    const anchors = [...anchorWrapper.querySelectorAll('.blog-post-content-anchors__link')];
    const [desktopAnchors, mobileAnchors] = [anchors.slice(0, Math.ceil(anchors.length / 2)), anchors.slice(Math.ceil(anchors.length / 2))];
    
    if (!mobileAnchors[idx]) {
      accordion?.append(anchor.cloneNode(true));
    } else {
      mobileAnchors[idx]?.before(anchor.cloneNode(true));
    }
    
    if (!desktopAnchors[idx]) {
      anchorWrapper.querySelector('.blog-post-content-anchors__social-icons')?.before(anchor.cloneNode(true));
    } else {
      desktopAnchors[idx]?.before(anchor.cloneNode(true));
    }

  });
}

function init() {
  autoAnchorsDetection();
  const containers = new Set(
    Array.from(document.querySelectorAll('.blog-post-content-anchors, .blog-post-content-anchors__content--accordion'))
      .map(el => el.closest('.blog-post-content-anchors') || el)
  );
  containers.forEach(container => setupBlogPostAnchorsObserver(/** @type {HTMLElement} */ (container)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
